/* <media-banner> — sticky media banner.

   1. Header offset: pins the panel just below a sticky navbar.
   2. Letter conveyor (scroll-scrubbed): every heading is split into letters
      (GSAP SplitText), each in its own clip mask. A ScrollTrigger timeline,
      scrubbed over the section's pinned travel, shows the first line fully
      revealed, then slides its letters up out of their masks; once a line is
      ~75% gone the next line's letters reveal in their place, and so on —
      reversing as you scroll back up. Tied to scroll, not time. (One heading =
      simple in/out.)
   3. Video: optional background video with a play/pause button.

   Progressive enhancement: with GSAP/ScrollTrigger/SplitText missing, reduced
   motion, or no heading, the heading is left visible and un-animated. Re-inits
   cleanly after a theme-editor re-render. */
if (!customElements.get('media-banner')) {
  class MediaBanner extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;

      this.panel = this.querySelector('.media-banner__sticky');
      if (!this.panel) return;

      this.syncHeaderOffset = this.syncHeaderOffset.bind(this);
      this.syncHeaderOffset();
      window.addEventListener('resize', this.syncHeaderOffset, { passive: true });
      window.addEventListener('load', this.syncHeaderOffset);

      this.initVideo();
      this.initReveal();
    }

    disconnectedCallback() {
      window.removeEventListener('resize', this.syncHeaderOffset);
      window.removeEventListener('load', this.syncHeaderOffset);
      if (this.video) {
        this.toggle.removeEventListener('click', this.onToggle);
        this.video.removeEventListener('play', this.syncVideoState);
        this.video.removeEventListener('pause', this.syncVideoState);
      }
      if (this.tl) {
        if (this.tl.scrollTrigger) this.tl.scrollTrigger.kill();
        this.tl.kill();
        this.tl = null;
      }
      if (this.splits) {
        this.splits.forEach(function (s) { s.revert(); });
        this.splits = null;
      }
    }

    /* Distance the sticky panel sits from the top of the viewport (its CSS
       `top`, i.e. header offset + configured gap). Drives where the scrub starts. */
    panelTop() {
      return parseFloat(getComputedStyle(this.panel).top) || 0;
    }

    /* Split the first heading into masked words and scrub them in/out on scroll. */
    initReveal() {
      var self = this;
      var reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      var ready = window.gsap && window.ScrollTrigger && window.SplitText;
      this.headings = Array.prototype.slice.call(
        this.querySelectorAll('.media-banner__heading')
      );
      if (!this.headings.length || reduce || !ready) return; // leave headings static

      var gsap = window.gsap;
      gsap.registerPlugin(window.ScrollTrigger, window.SplitText);

      // Keep ScrollTrigger in step with Lenis' smoothed scroll (wire once).
      if (window.lenis && !window.__mbLenisSynced) {
        window.__mbLenisSynced = true;
        window.lenis.on('scroll', window.ScrollTrigger.update);
      }

      var build = function () {
        // Split each heading into chars (words kept intact so lines still wrap).
        // Chars are gathered top-to-bottom so the stagger cascades line by line.
        self.splits = self.headings.map(function (h) {
          return window.SplitText.create(h, {
            type: 'words,chars',
            mask: 'chars',        // wrap each letter in its own overflow-clip mask
            charsClass: 'reveal-char',
            wordsClass: 'reveal-word',
          });
        });
        var lines = self.splits.map(function (s) { return s.chars; });

        // Linear (ease 'none') timeline so it maps 1:1 to scroll while scrubbing;
        // each tween carries its own power3 ease for soft motion.
        self.tl = gsap.timeline({
          defaults: { ease: 'none' },
          scrollTrigger: {
            trigger: self,
            start: function () { return 'top top+=' + self.panelTop(); }, // pins → progress 0
            end: 'bottom bottom',                                         // releases → progress 1
            scrub: 1,                  // ~1s catch-up smoothing, follows scroll both ways
            invalidateOnRefresh: true, // recompute on resize / font load
          },
        });

        var STAGGER = 0.06, DUR = 1, HOLD = 0.4, OVERLAP = 0.75;

        // Each line's letters reveal out of their own clip masks in place — no
        // whole-line translate (that was lifting the sentences inconsistently).
        // Parked = letters hidden just below their masks.
        var park = function (chars) {
          gsap.set(chars, { yPercent: 110, opacity: 0 });
        };
        // IN: letters rise up out of their masks, staggered.
        var animIn = function (chars, at) {
          self.tl.to(chars, { yPercent: 0, opacity: 1, stagger: STAGGER, duration: DUR, ease: 'power3.out' }, at);
        };
        // OUT: letters slide up out of their masks, staggered.
        var animOut = function (chars, at) {
          self.tl.to(chars, { yPercent: -110, opacity: 0, stagger: STAGGER, duration: DUR, ease: 'power3.in' }, at);
        };

        if (lines.length === 1) {
          park(lines[0]);
          animIn(lines[0], 0);
          self.tl.to({}, { duration: HOLD });
          animOut(lines[0], '>');
          return;
        }

        // First line is already fully revealed; the rest wait parked below.
        gsap.set(lines[0], { yPercent: 0, opacity: 1 });
        for (var j = 1; j < lines.length; j++) park(lines[j]);

        // Conveyor: the current line animates OUT, and the next line only STARTS
        // animating IN once the current line is ~75% gone. A line's OUT lives in
        // the NEXT step, so it can't begin until the line before it has gone.
        for (var i = 0; i < lines.length - 1; i++) {
          var step = 'step' + i;
          self.tl.addLabel(step);
          animOut(lines[i], step); // current line leaves
          // Fully gone when the last letter finishes: DUR + stagger across letters.
          var outDur = DUR + STAGGER * Math.max(0, lines[i].length - 1);
          animIn(lines[i + 1], step + '+=' + (outDur * OVERLAP));
          self.tl.to({}, { duration: HOLD }); // dwell before the next step
        }
      };

      // Split after fonts load so word boxes are measured correctly.
      if (document.fonts && document.fonts.ready) {
        document.fonts.ready.then(build);
      } else {
        build();
      }
    }

    /* Background video + rounded play/pause button. */
    initVideo() {
      this.video = this.querySelector('.media-banner__video');
      this.toggle = this.querySelector('[data-media-banner-toggle]');
      if (!this.video || !this.toggle) {
        this.video = null;
        return;
      }

      this.onToggle = this.onToggle.bind(this);
      this.syncVideoState = this.syncVideoState.bind(this);
      this.toggle.addEventListener('click', this.onToggle);
      this.video.addEventListener('play', this.syncVideoState);
      this.video.addEventListener('pause', this.syncVideoState);
      this.syncVideoState();
    }

    onToggle() {
      if (this.video.paused) {
        var playing = this.video.play();
        if (playing && playing.catch) playing.catch(function () { /* autoplay blocked */ });
      } else {
        this.video.pause();
      }
    }

    syncVideoState() {
      var paused = this.video.paused;
      if (paused) {
        this.setAttribute('data-video-paused', '');
      } else {
        this.removeAttribute('data-video-paused');
      }
      this.toggle.setAttribute('aria-label', paused ? 'Play video' : 'Pause video');
    }

    /* Pin the panel clear of a sticky header. Falls back to 0 when the header
       isn't sticky, so the panel just uses its own configured gap. */
    syncHeaderOffset() {
      var header = document.querySelector('[data-header-sticky]');
      var height = header ? Math.round(header.getBoundingClientRect().height) : 0;
      this.style.setProperty('--mb-header-offset', height + 'px');
    }
  }

  customElements.define('media-banner', MediaBanner);
}
